import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import { IPInfo } from '../../api/sienaguardService';

// Asegúrate de importar los estilos de Leaflet globalmente, por ejemplo:
// import 'leaflet/dist/leaflet.css';

interface IPCountriesMapProps {
  ipInfoList: IPInfo[];
}

type CountryData = {
  [key: string]: {
    count: number;
    domains: string[];
  };
};

// Función de normalización para evitar discrepancias en nombres de países
const normalizeCountryName = (name: string): string => {
  const lower = name.toLowerCase().trim();
  if (lower.includes('united states')) {
    return 'United States';
  }
  // Agrega más normalizaciones si es necesario
  return name.trim();
};

const IPCountriesMap: React.FC<IPCountriesMapProps> = ({ ipInfoList }) => {
  const { t } = useTranslation();
  const [geoJsonData, setGeoJsonData] = useState<any>(null);

  // Cargar el archivo GeoJSON desde la carpeta public
  useEffect(() => {
    fetch('/assets/geojson/countries.geojson')
      .then((res) => res.json())
      .then((data) => setGeoJsonData(data))
      .catch((err) => console.error('Error al cargar el GeoJSON:', err));
  }, []);

  // Calcular los datos por país (agrupados)
  const countryData = useMemo<CountryData>(() => {
    const map: CountryData = {};
    ipInfoList.forEach((info) => {
      // Si info.country es undefined, se usa un valor por defecto obtenido de las traducciones
      const rawCountry: string = info.country || t('geo.unknown', { defaultValue: 'Desconocido' });
      const country = normalizeCountryName(rawCountry);
      if (!map[country]) {
        map[country] = { count: 0, domains: [] };
      }
      map[country].count += 1;
      map[country].domains.push(info.domain);
    });
    return map;
  }, [ipInfoList, t]);

  // Función para definir el estilo de cada feature en el GeoJSON
  const styleFeature = (feature: any) => {
    const countryName = feature.properties.ADMIN || t('geo.unknown', { defaultValue: 'Desconocido' });
    const normalizedFeatureCountry = normalizeCountryName(countryName);
    
    // Comprobar si existe data para ese país
    let hasData = false;
    for (const key of Object.keys(countryData)) {
      if (normalizeCountryName(key) === normalizedFeatureCountry) {
        hasData = true;
        break;
      }
    }
    return {
      fillColor: hasData ? 'red' : '#ccc',
      weight: 1,
      color: '#666',
      fillOpacity: hasData ? 0.7 : 0.2,
    };
  };

  // Función para configurar el popup de cada feature
  const onEachFeature = (feature: any, layer: L.Layer) => {
    const countryName = feature.properties.ADMIN || t('geo.unknown', { defaultValue: 'Desconocido' });
    const normalizedFeatureCountry = normalizeCountryName(countryName);

    const found = Object.entries(countryData).find(
      ([key]) => normalizeCountryName(key) === normalizedFeatureCountry
    );
    if (found) {
      const [country, info] = found;
      const domainList = info.domains.join('<br/>');
      const popupContent = `
        <div>
          <strong>${country}</strong> (${info.count} ${t('geo.attempts', { defaultValue: 'intentos' })})<br/>
          <strong>${t('geo.domains', { defaultValue: 'Dominios' })}:</strong><br/>
          ${domainList}
        </div>
      `;
      layer.bindPopup(popupContent);
    } else {
      layer.bindPopup(`
        <div>
          <strong>${countryName}</strong><br/>
          ${t('geo.no_attempts', { defaultValue: 'Sin intentos' })}
        </div>
      `);
    }
  };

  // Forzar que el GeoJSON se re-renderice al cambiar los datos, usando como key la representación JSON de countryData
  const geoJsonKey = JSON.stringify(countryData);

  const initialPosition: [number, number] = [20, 0];
  const initialZoom = 2;

  return (
    <div style={{ width: '100%', height: '500px' }}>
      <MapContainer center={initialPosition} zoom={initialZoom} minZoom={2} maxBounds={[[-90, -180], [90, 180]]} maxBoundsViscosity={1.0} style={{ width: '100%', height: '100%' }}>
        <TileLayer
          attribution={`&copy; <a href="https://www.openstreetmap.org/">${t('geo.openstreetmap', { defaultValue: 'OpenStreetMap' })}</a> contributors`}
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {geoJsonData && (
          <GeoJSON
            key={geoJsonKey}
            data={geoJsonData}
            style={styleFeature}
            onEachFeature={onEachFeature}
          />
        )}
      </MapContainer>
    </div>
  );
};

export default IPCountriesMap;
